import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Autocomplete,
  TableSortLabel,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { trainersAPI } from '../services/api';
import { actionsKabobCellSx, actionsKabobHeaderSx } from '../theme/tableStyles';
import {
  tablePageRootSx,
  tablePageHeaderStickySx,
  tablePageScrollRegionSx,
  tablePageTableContainerSx,
} from '../theme/tablePageLayout';
import { US_STATES } from '../constants/usStates';
import { createPrefixFilter } from '../utils/autocompleteFilters';
import ConfirmDialog from '../components/ConfirmDialog';
import RowActionsMenu from '../components/RowActionsMenu';
import useRefreshOnFocus from '../utils/useRefreshOnFocus';
import { toArray } from '../utils/apiData';
import { useNotify } from '../context/NotifyContext';
import errorMessage from '../utils/errorMessage';
import { TableSkeletonRows } from '../components/LoadingSkeletons';

// Phone number formatting function
const formatPhoneNumber = (phone) => {
  if (!phone) return '-';
  
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '');
  
  // Check if it's a valid US phone number (10 digits)
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  
  // Check if it's a valid US phone number with country code (11 digits starting with 1)
  if (cleaned.length === 11 && cleaned[0] === '1') {
    const withoutCountryCode = cleaned.slice(1);
    return `(${withoutCountryCode.slice(0, 3)}) ${withoutCountryCode.slice(3, 6)}-${withoutCountryCode.slice(6)}`;
  }
  
  // Return original if it doesn't match expected formats
  return phone;
};

const TrainersPage = () => {
  const notify = useNotify();
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTrainer, setEditingTrainer] = useState(null);
  const [sortBy, setSortBy] = useState('business_name');
  const [sortOrder, setSortOrder] = useState('asc');

  const [formData, setFormData] = useState({
    business_name: '',
    trainer_name: '',
    phone: '',
    email: '',
    emergency_phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    specialties: '',
    notes: '',
  });

  const loadTrainers = useCallback(async () => {
    setLoadError('');
    try {
      const params = {
        sort_by: sortBy,
        sort_order: sortOrder,
      };
      const response = await trainersAPI.getAll(params);
      setTrainers(toArray(response.data, 'trainers'));
    } catch (error) {
      console.error('Error loading trainers:', error);
      // The previous list is left in place on purpose: a failed refresh should say so
      // rather than blank the page out into something indistinguishable from "no trainers".
      setLoadError(error?.response?.data?.error || 'Unable to load trainers. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [sortBy, sortOrder]);

  useEffect(() => {
    loadTrainers();
  }, [loadTrainers]);

  useRefreshOnFocus(loadTrainers);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const handleOpenDialog = (trainer = null) => {
    if (trainer) {
      setEditingTrainer(trainer);
      setFormData(trainer);
    } else {
      setEditingTrainer(null);
      setFormData({
        business_name: '',
        trainer_name: '',
        phone: '',
        email: '',
        emergency_phone: '',
        address: '',
        city: '',
        state: '',
        zip_code: '',
        specialties: '',
        notes: '',
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingTrainer(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    try {
      if (editingTrainer) {
        await trainersAPI.update(editingTrainer.id, formData);
      } else {
        await trainersAPI.create(formData);
      }
      handleCloseDialog();
      loadTrainers();
    } catch (error) {
      console.error('Error saving trainer:', error);
      notify.error(errorMessage(error, 'Error saving trainer'));
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDeleteClick = (trainer) => setDeleteTarget(trainer);
  const handleDeleteCancel = () => setDeleteTarget(null);

  const handleDeleteConfirm = async () => {
    try {
      await trainersAPI.delete(deleteTarget.id);
      setDeleteTarget(null);
      loadTrainers();
    } catch (error) {
      console.error('Error deleting trainer:', error);
      notify.error(errorMessage(error, 'Error deleting trainer'));
    }
  };


  return (
    <Box sx={tablePageRootSx}>
      <Box sx={tablePageHeaderStickySx}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: { xs: 'flex-start', sm: 'space-between' },
          alignItems: 'center',
          gap: { xs: 1.5, sm: 0 },
          mb: 3,
        }}
      >
        <Typography variant="h4">Trainers</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            minWidth: { xs: 0, sm: 'auto' },
            width: { xs: 32, sm: 'auto' },
            height: { xs: 32, sm: 'auto' },
            px: { xs: 0, sm: 2 },
            '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } },
          }}
        >
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
            Add Trainer
          </Box>
        </Button>
      </Box>
      {loadError ? (
        <Alert severity="error" sx={{ mb: 0.5 }}>
          {loadError}
        </Alert>
      ) : null}
      </Box>

      <Box sx={tablePageScrollRegionSx}>
        <TableContainer component={Paper} sx={tablePageTableContainerSx}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={actionsKabobHeaderSx}>Actions</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'business_name'}
                  direction={sortBy === 'business_name' ? sortOrder : 'asc'}
                  onClick={() => handleSort('business_name')}
                >
                  Business Name
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'trainer_name'}
                  direction={sortBy === 'trainer_name' ? sortOrder : 'asc'}
                  onClick={() => handleSort('trainer_name')}
                >
                  Trainer Name
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'phone'}
                  direction={sortBy === 'phone' ? sortOrder : 'asc'}
                  onClick={() => handleSort('phone')}
                >
                  Phone
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'email'}
                  direction={sortBy === 'email' ? sortOrder : 'asc'}
                  onClick={() => handleSort('email')}
                >
                  Email
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortBy === 'city'}
                  direction={sortBy === 'city' ? sortOrder : 'asc'}
                  onClick={() => handleSort('city')}
                >
                  City, State
                </TableSortLabel>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && <TableSkeletonRows columns={6} />}
            {trainers.map((trainer) => (
              <TableRow key={trainer.id}>
                <TableCell align="center" sx={actionsKabobCellSx}>
                  <RowActionsMenu
                    actions={[
                      { key: 'edit', label: 'Edit', icon: <EditIcon fontSize="small" />, onClick: () => handleOpenDialog(trainer) },
                      { key: 'delete', label: 'Delete', icon: <DeleteIcon fontSize="small" />, onClick: () => handleDeleteClick(trainer), color: 'error' },
                    ]}
                  />
                </TableCell>
                <TableCell>{trainer.business_name || trainer.trainer_name || '-'}</TableCell>
                <TableCell>{trainer.trainer_name || '-'}</TableCell>
                <TableCell>{formatPhoneNumber(trainer.phone)}</TableCell>
                <TableCell>{trainer.email || '-'}</TableCell>
                <TableCell>
                  {trainer.city && trainer.state ? `${trainer.city}, ${trainer.state}` : '-'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      </Box>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingTrainer ? 'Edit Trainer' : 'Add New Trainer'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="business_name"
                  label="Business Name"
                  value={formData.business_name}
                  onChange={handleInputChange}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="trainer_name"
                  label="Trainer Name"
                  value={formData.trainer_name}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="phone"
                  label="Phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="email"
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  size="small"
                  name="emergency_phone"
                  label="Emergency Phone"
                  value={formData.emergency_phone}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  name="address"
                  label="Address"
                  value={formData.address}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  name="city"
                  label="City"
                  value={formData.city}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  options={US_STATES}
                  getOptionLabel={(option) => option.name}
                  filterOptions={createPrefixFilter((option) => option.name)}
                  value={US_STATES.find(state => state.code === formData.state) || null}
                  onChange={(event, newValue) => {
                    setFormData((prev) => ({ ...prev, state: newValue ? newValue.code : '' }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      size="small"
                      {...params}
                      label="State"
                      fullWidth
                    />
                  )}
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    return (
                      <Box component="li" key={key} {...otherProps}>
                        {option.code} - {option.name}
                      </Box>
                    );
                  }}
                  isOptionEqualToValue={(option, value) => option.code === value?.code}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  size="small"
                  name="zip_code"
                  label="Zip Code"
                  value={formData.zip_code}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  name="specialties"
                  label="Specialties"
                  value={formData.specialties}
                  onChange={handleInputChange}
                  fullWidth
                  placeholder="e.g., Reining, Cutting, Western Pleasure, Starting Colts"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  size="small"
                  name="notes"
                  label="Notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  multiline
                  rows={3}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingTrainer ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Deactivate Trainer"
        message={`Deactivate trainer "${deleteTarget?.business_name || deleteTarget?.trainer_name}"?`}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        confirmLabel="Deactivate"
        confirmColor="warning"
      />
    </Box>
  );
};

export default TrainersPage;

